import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Header from "@/components/cre/Header";
import Footer from "@/components/cre/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { UserPlus } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function Register() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setBusy(true);
    const res = await register(email.trim(), password, name.trim() || null);
    setBusy(false);
    if (res.ok) {
      toast.success(
        res.user?.role === "admin"
          ? "Admin account created — you are the first user."
          : "Welcome aboard"
      );
      navigate("/dashboard", { replace: true });
    } else {
      setError(res.error || "Registration failed");
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <Header />
      <div className="flex-1">
        <div className="max-w-md mx-auto px-6 md:px-8 py-14 md:py-20">
          <div className="cre-overline text-sky-700">Account</div>
          <h1 className="font-heading text-3xl font-black tracking-tight mt-1 text-slate-900">
            Create your account
          </h1>
          <p className="mt-2 text-sm text-slate-600">
            Save your incident analyses for 7 days, delete them anytime, and
            see your reasoning history in one place.
          </p>

          <form
            onSubmit={submit}
            className="cre-card p-6 mt-8 space-y-4"
            data-testid="register-form"
          >
            <div>
              <Label className="cre-overline">Name (optional)</Label>
              <Input
                type="text"
                autoComplete="name"
                data-testid="register-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="mt-1.5 h-11 bg-white border-slate-200 focus-visible:ring-sky-500"
              />
            </div>
            <div>
              <Label className="cre-overline">Email</Label>
              <Input
                type="email"
                autoComplete="email"
                required
                data-testid="register-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1.5 h-11 bg-white border-slate-200 focus-visible:ring-sky-500"
              />
            </div>
            <div>
              <Label className="cre-overline">Password</Label>
              <Input
                type="password"
                autoComplete="new-password"
                required
                minLength={8}
                data-testid="register-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1.5 h-11 bg-white border-slate-200 focus-visible:ring-sky-500"
              />
            </div>
            <div>
              <Label className="cre-overline">Confirm password</Label>
              <Input
                type="password"
                autoComplete="new-password"
                required
                minLength={8}
                data-testid="register-confirm"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="mt-1.5 h-11 bg-white border-slate-200 focus-visible:ring-sky-500"
              />
            </div>
            {error ? (
              <div
                data-testid="register-error"
                className="text-xs text-rose-700 border border-rose-200 bg-rose-50 rounded-md px-3 py-2"
              >
                {error}
              </div>
            ) : null}
            <Button
              type="submit"
              disabled={busy}
              data-testid="register-submit"
              className="w-full cre-cta-primary rounded-lg font-semibold h-11"
            >
              <UserPlus className="h-4 w-4 mr-2" strokeWidth={2} />
              {busy ? "Creating…" : "Create account"}
            </Button>
            <div className="text-center text-sm text-slate-600">
              Already have an account?{" "}
              <Link
                to="/login"
                data-testid="register-goto-login"
                className="text-sky-700 hover:text-sky-600 underline-offset-4 hover:underline font-semibold"
              >
                Sign in
              </Link>
            </div>
          </form>
        </div>
      </div>
      <Footer />
    </div>
  );
}
